/**
 * RelatedModules — la pestaña Relacionados como estructura ÚNICA entre
 * pantallas (Contactos, Prospectos, Proyectos): una rejilla de tarjetas, una
 * por módulo (Proyectos, Reportes, Facturación, Tareas, Formularios), SIEMPRE
 * las cinco presentes aunque estén vacías, cada una con el "+" en la cabecera
 * que lleva al flujo de crear con el cliente precargado.
 *
 * Los contenidos por defecto consultan por contactId. Una página puede
 * sustituir contenido y/o acción de crear de un módulo (override) cuando su
 * data viene de otra fuente (ej: prospecto consulta proyectos por leadId).
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import React from 'react';
import { balancedColumns } from '../../lib/balancedColumns.js';
import { DetailSection } from '../ui/DetailSection.js';
import { RelatedCreate } from '../ui/RelatedCreate.js';
import { Pressable } from '../ui/Pressable.js';
import { LoadingState } from '../ui/LoadingState.js';
import { EmptyState } from '../ui/EmptyState.js';
import { Pill, type PillTone } from '../PillGroup.js';
import { useContactProjects } from '../../hooks/useProjects.js';
import { useReports } from '../../hooks/useReports.js';
import { useTasks } from '../../hooks/useTasks.js';
import { useContactSubmissions } from '../../hooks/useContactSubmissions.js';
import { useEstimates, useInvoices, useAccounts, type Estimate, type Invoice } from '../../hooks/useBilling.js';
import { ESTIMATE_STATUS_TONES } from '../billing/EstimateDetail.js';
import { formatMoney } from '../../lib/money.js';
import { parseServerDate } from '../../lib/date.js';

const PROJECT_STATUS_TONES: Record<string, PillTone> = {
  draft:         'neutral',
  briefed:       'blue',
  in_design:     'cyan',
  client_review: 'amber',
  revisions:     'warn',
  approved:      'success',
  delivered:     'info',
  archived:      'muted',
};

const TASK_STATUS_TONES: Record<string, PillTone> = {
  pending: 'neutral',
  needs_approval: 'amber',
  approved: 'cyan',
  in_progress: 'blue',
  done: 'success',
  failed: 'danger',
  blocked: 'muted',
};
const TASK_PRIORITY_TONES: Record<string, PillTone> = { alta: 'danger', media: 'amber', baja: 'muted' };

const REPORT_STATUS_TONES: Record<string, PillTone> = {
  pending: 'muted',
  generating: 'amber',
  ready: 'cyan',
  failed: 'danger',
};

/* Fila estándar de las tarjetas de Relacionados: título (+ subtítulo
   opcional) a la izquierda, pills a la derecha, caja bordeada. */
function RelatedRow({ title, subtitle, pills, onPress }: { title: string; subtitle?: string; pills?: ReactNode; onPress: () => void }) {
  return (
    <Pressable fullWidth onPress={onPress}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--evo-subrow-py) var(--evo-subrow-px)', borderRadius: 'var(--evo-r)', border: '1px solid var(--evo-stroke)', gap: 'var(--evo-subrow-gap)' }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title}
          </span>
          {subtitle != null && (
            <span style={{ fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {subtitle}
            </span>
          )}
        </div>
        {pills}
      </div>
    </Pressable>
  );
}

function RowList({ children }: { children: ReactNode }) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>{children}</div>;
}

export function RelatedProjects({ contactId }: { contactId: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useContactProjects(contactId);
  const items = data?.items ?? [];

  if (isLoading) return <LoadingState size="sm" />;
  if (items.length === 0) return <EmptyState title={t('projects.empty')} />;
  return (
    <RowList>
      {items.map(p => (
        <RelatedRow
          key={p.id}
          title={p.name}
          onPress={() => navigate(`/proyectos/${p.slug}`)}
          pills={
            <Pill tone={PROJECT_STATUS_TONES[p.status] ?? 'neutral'} variant="subtle" size="sm">
              {t(`projects.status.${p.status}`, { defaultValue: p.status })}
            </Pill>
          }
        />
      ))}
    </RowList>
  );
}

export function RelatedTasks({ contactId }: { contactId: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useTasks({ contactId });
  const items = data?.tasks ?? [];

  if (isLoading) return <LoadingState size="sm" />;
  if (items.length === 0) return <EmptyState title={t('tasks.empty')} />;
  return (
    <RowList>
      {items.map(tk => (
        <RelatedRow
          key={tk.id}
          title={tk.title}
          subtitle={`${t(`tasks.type.${tk.type}`, { defaultValue: tk.type })}${tk.nextRunAt ? ` · ${new Date(tk.nextRunAt).toLocaleDateString()}` : ''}`}
          onPress={() => navigate(`/tasks?open=${tk.id}`)}
          pills={
            <>
              <Pill tone={TASK_PRIORITY_TONES[tk.priority] ?? 'muted'} variant="subtle" size="sm">
                {t(`tasks.priority.${tk.priority}`, { defaultValue: tk.priority })}
              </Pill>
              <Pill tone={TASK_STATUS_TONES[tk.status] ?? 'neutral'} variant="subtle" size="sm">
                {t(`tasks.status.${tk.status}`, { defaultValue: tk.status })}
              </Pill>
            </>
          }
        />
      ))}
    </RowList>
  );
}

export function RelatedForms({ contactId }: { contactId: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useContactSubmissions(contactId);
  const items = data?.submissions ?? [];

  if (isLoading) return <LoadingState size="sm" />;
  if (items.length === 0) return <EmptyState title={t('contacts.forms.empty')} />;
  return (
    <RowList>
      {items.map(sub => (
        <RelatedRow
          key={sub.id}
          title={sub.formName}
          subtitle={parseServerDate(sub.createdAt).toLocaleDateString()}
          onPress={() => navigate(`/formularios?respuesta=${sub.id}`)}
          pills={
            /* Sin revisar es lo único que pide acción; lo revisado no
               necesita una pill que diga "ya está". */
            sub.reviewedAt == null
              ? <Pill tone="cyan" variant="subtle" size="sm">{t('contacts.forms.unreviewed')}</Pill>
              : undefined
          }
        />
      ))}
    </RowList>
  );
}

export function RelatedReports({ contactId }: { contactId: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useReports({ contactId });
  const items = data?.reports ?? [];

  if (isLoading) return <LoadingState size="sm" />;
  if (items.length === 0) return <EmptyState title={t('reports.noReports')} />;
  return (
    <RowList>
      {items.map(r => (
        <RelatedRow
          key={r.id}
          title={r.title}
          subtitle={`${t(`reports.types.${r.type}.label`)} · ${parseServerDate(r.createdAt).toLocaleDateString()}`}
          onPress={() => navigate(`/reports/${r.id}`)}
          pills={
            <Pill tone={REPORT_STATUS_TONES[r.status] ?? 'muted'} variant="subtle" size="sm">
              {t(`reports.status.${r.status}`)}
            </Pill>
          }
        />
      ))}
    </RowList>
  );
}

// Cotizaciones y facturas como dos sub-secciones etiquetadas (no un toggle):
// ambas visibles a la vez, cada una con su EmptyState propio.
export function RelatedBilling({ contactId }: { contactId: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: estimatesData, isLoading: estimatesLoading } = useEstimates({ contactId });
  const { data: invoicesData, isLoading: invoicesLoading } = useInvoices({ contactId });
  const { data: accountsData } = useAccounts();
  const estimates = estimatesData?.estimates ?? [];
  const invoices = invoicesData?.invoices ?? [];

  function currencyForAccount(accountId: number): string {
    return accountsData?.accounts.find(a => a.id === accountId)?.currency ?? 'usd';
  }

  function renderRow(type: 'estimate' | 'invoice', row: Estimate | Invoice, currency: string) {
    return (
      <RelatedRow
        key={`${type}-${row.id}`}
        title={row.number ?? `#${row.id}`}
        subtitle={`${formatMoney(row.total, currency)} · ${new Date(row.createdAt).toLocaleDateString()}`}
        onPress={() => navigate(`/facturacion/${type}/${row.id}`)}
        pills={
          <>
            {/* Qué documento es — la lista es una sola, así que el tipo tiene
                que leerse en la fila. */}
            <Pill tone="muted" variant="outline" size="sm">
              {t(`billing.facturacion.${type === 'estimate' ? 'typeEstimate' : 'typeInvoice'}`)}
            </Pill>
            <Pill tone={ESTIMATE_STATUS_TONES[row.status] ?? 'neutral'} variant="subtle" size="sm">
              {t(`billing.${type === 'estimate' ? 'estimateStatus' : 'invoiceStatus'}.${row.status}`, { defaultValue: row.status })}
            </Pill>
          </>
        }
      />
    );
  }

  if (estimatesLoading || invoicesLoading) return <LoadingState size="sm" />;

  // UNA sola lista, ordenada por fecha. Antes eran dos bloques con su propio
  // título y su propio vacío: en un contacto con una cotización y ninguna
  // factura se veía una lista y un cartel de "no hay facturas", y el orden
  // real de los hechos (cotizo, facturo) se perdía. El tipo de documento va
  // como etiqueta en la fila, que es donde se lo busca.
  const documentos = [
    ...estimates.map(e => ({ tipo: 'estimate' as const, row: e as Estimate | Invoice, currency: currencyForAccount(e.accountId) })),
    ...invoices.map(i => ({ tipo: 'invoice' as const, row: i as Estimate | Invoice, currency: i.currency })),
  ].sort((a, b) => b.row.createdAt - a.row.createdAt);

  if (documentos.length === 0) return <EmptyState title={t('billing.facturacion.noDocuments')} />;

  return (
    <RowList>
      {documentos.map(d => renderRow(d.tipo, d.row, d.currency))}
    </RowList>
  );
}

// ── La rejilla ────────────────────────────────────────────────────────────────

export type RelatedModuleKey = 'projects' | 'reports' | 'billing' | 'tasks' | 'forms';

interface ModuleOverride {
  /** Contenido propio de la página (ej: proyectos por leadId). */
  content?: ReactNode;
  /** Acción de crear propia (ej: convertir prospecto a contacto primero). */
  onCreate?: () => void;
}

interface RelatedModulesGridProps {
  /** null → los módulos por contacto se muestran vacíos. */
  contactId: number | null;
  contactName: string;
  overrides?: Partial<Record<RelatedModuleKey, ModuleOverride>>;
}

export function RelatedModulesGrid({ contactId, contactName, overrides }: RelatedModulesGridProps) {
  const { t } = useTranslation();

  const params = contactId != null
    ? `?nuevo=1&contactId=${contactId}&contactName=${encodeURIComponent(contactName)}`
    : '?nuevo=1';

  const MODULES: { key: RelatedModuleKey; title: string; createLabel: string; to: string; content: ReactNode; emptyTitle: string }[] = [
    {
      key: 'projects', title: t('contacts.tabs.projects'),
      createLabel: t('projects.newProject'), to: `/proyectos${params}`,
      content: contactId != null ? <RelatedProjects contactId={contactId} /> : null,
      emptyTitle: t('projects.empty'),
    },
    {
      key: 'reports', title: t('contacts.tabs.reports'),
      createLabel: t('reports.newReport', { defaultValue: 'Nuevo reporte' }), to: `/reports${params}`,
      content: contactId != null ? <RelatedReports contactId={contactId} /> : null,
      emptyTitle: t('reports.noReports'),
    },
    {
      key: 'billing', title: t('contacts.tabs.facturacion'),
      createLabel: t('billing.facturacion.newEstimate', { defaultValue: 'Nueva cotización' }), to: `/facturacion${params}`,
      content: contactId != null ? <RelatedBilling contactId={contactId} /> : null,
      emptyTitle: t('billing.facturacion.noEstimates'),
    },
    {
      key: 'tasks', title: t('contacts.tabs.tasks'),
      createLabel: t('tasks.modal.title', { defaultValue: 'Nueva tarea' }), to: `/tasks${params}`,
      content: contactId != null ? <RelatedTasks contactId={contactId} /> : null,
      emptyTitle: t('tasks.empty'),
    },
    {
      key: 'forms', title: t('contacts.tabs.formularios'),
      createLabel: t('forms.list.newForm', { defaultValue: 'Nuevo formulario' }), to: `/formularios${params}`,
      content: contactId != null ? <RelatedForms contactId={contactId} /> : null,
      emptyTitle: t('contacts.forms.empty'),
    },
  ];

  return (
    <div className="evo-related-grid" style={{ '--related-cols': balancedColumns(MODULES.length) } as React.CSSProperties}>
      {MODULES.map(m => {
        const ov = overrides?.[m.key];
        return (
          <DetailSection
            key={m.key}
            title={m.title}
            titleRight={
              ov?.onCreate
                ? <RelatedCreate label={m.createLabel} onClick={ov.onCreate} />
                : <RelatedCreate label={m.createLabel} to={m.to} />
            }
          >
            {ov?.content ?? m.content ?? <EmptyState title={m.emptyTitle} />}
          </DetailSection>
        );
      })}
    </div>
  );
}
